import apiClient, { getApiEndpoint } from '@/lib/apiClient';
import { handleApiError } from '@/lib/apiErrorUtils';
import { ContractCancellationDetails } from './types';

/**
 * Retrieves cancellation details for a specific contract.
 *
 * This function queries the contracts API to get detailed cancellation information
 * including refund amounts, fees, and other cancellation-related data.
 *
 * @param {string} contractNumber - The contract number to fetch cancellation details for
 * @returns {Promise<ContractCancellationDetails>} A promise that resolves to cancellation details
 * @throws {Error} Throws an error with a user-friendly message if the API request fails
 */
export async function getCancellationDetails(
  contractNumber: string
): Promise<ContractCancellationDetails> {
  try {
    const endpoint = getApiEndpoint(
      `/contracts/cancellation/${contractNumber}`
    );
    const response = await apiClient.get<ContractCancellationDetails>(endpoint);

    return response.data;
  } catch (err) {
    const userFriendlyMessage = handleApiError(
      err,
      'fetching contract cancellation details',
      { contractNumber }
    );
    throw new Error(userFriendlyMessage);
  }
}

/**
 * Extensible function for fetching contract details based on status.
 *
 * This provides a future-ready pattern for handling different contract statuses
 * beyond just cancellation details. Currently only supports 'cancelled' status.
 *
 * @param {string} contractNumber - The contract number to fetch details for
 * @param {string} status - The contract status ('cancelled', future: 'expired', 'active', etc.)
 * @returns {Promise<ContractCancellationDetails>} Contract details based on status
 * @throws {Error} Throws an error if status is not supported or API request fails
 */
export async function getContractDetails(
  contractNumber: string,
  status: string
): Promise<ContractCancellationDetails> {
  switch (status.toLowerCase()) {
    case 'cancelled':
      return getCancellationDetails(contractNumber);
    // Future cases for extensibility:
    // case 'expired':
    //   return getExpiredContractDetails(contractNumber);
    // case 'active':
    //   return getActiveContractDetails(contractNumber);
    default:
      throw new Error(`Unsupported contract status: ${status}`);
  }
}
